import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from 'convex/react';
import { Button, FragmentsLogo, Input } from '@fragments-sdk/ui';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { useClerkUser } from '@/hooks/useClerkUser';
import { useCreateOrgForm } from '@/hooks/useCreateOrgForm';
import { ACTIVE_ORG_STORAGE_KEY } from '@/hooks/useOrgContext';
import { buildDashboardPath } from '@/lib/routes';
import s from './claim.module.scss';

export const Route = createFileRoute('/claim/$claimToken')({
  component: ClaimReadinessPage,
});

function claimPath(token: string) {
  return `/claim/${encodeURIComponent(token)}`;
}

function ClaimReadinessPage() {
  const { claimToken } = Route.useParams();
  const navigate = useNavigate();
  const { user, isLoaded } = useClerkUser();
  const clerkUserId = user?.id;
  const [draft, setDraft] = React.useState<ClaimDraft | null | undefined>();
  React.useEffect(() => {
    let cancelled = false;
    setDraft(undefined);
    fetch(`/api/registrar/claim?token=${encodeURIComponent(claimToken)}`)
      .then(async (response) => {
        if (!response.ok) return null;
        return (await response.json()) as ClaimDraft;
      })
      .then((nextDraft) => {
        if (!cancelled) setDraft(nextDraft);
      })
      .catch(() => {
        if (!cancelled) setDraft(null);
      });
    return () => {
      cancelled = true;
    };
  }, [claimToken]);
  const memberships = useQuery(
    api.memberships.listByUser,
    clerkUserId ? { clerkUserId } : 'skip',
  );
  const claimToOrg = useMutation(api.registrar.claimToOrg);
  const orgForm = useCreateOrgForm();
  const [error, setError] = React.useState<string | null>(null);
  const [claimingOrgId, setClaimingOrgId] = React.useState<Id<'orgs'> | null>(
    null,
  );
  const [claimed, setClaimed] = React.useState<{
    orgSlug: string | null;
    projectSlug: string | null;
  } | null>(null);

  async function finishClaim(orgId: Id<'orgs'>) {
    if (!clerkUserId) return;
    setError(null);
    setClaimingOrgId(orgId);
    try {
      const result = await claimToOrg({
        token: claimToken,
        orgId,
        clerkUserId,
      });
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
      setClaimed({
        orgSlug: result.orgSlug,
        projectSlug: result.projectSlug,
      });
    } catch (claimError) {
      setError(
        claimError instanceof Error ? claimError.message : String(claimError),
      );
    } finally {
      setClaimingOrgId(null);
    }
  }

  async function createOrgAndClaim() {
    const result = await orgForm.submit();
    if (!result) return;
    await finishClaim(result.org._id);
  }

  const report = draft?.report;
  const summary = report?.summary as
    | {
        inventory?: { components?: number; usageGuidance?: number };
        metrics?: {
          apiCoverage?: number;
          exampleCoverage?: number;
          tokenAdoption?: number | null;
          governanceFindings?: number | null;
        };
        topFindings?: Array<{ ruleId: string; occurrences: number }>;
      }
    | undefined;

  if (draft === undefined || !isLoaded) {
    return <ClaimShell title="Loading readiness claim..." />;
  }

  if (!draft || !report) {
    return (
      <ClaimShell
        kicker="Claim expired"
        title="This report is no longer available."
      >
        <p className={s.bodyText}>
          This readiness claim is no longer available. Run the readiness command
          again to create a fresh claim link.
        </p>
      </ClaimShell>
    );
  }

  if (!clerkUserId) {
    const next = claimPath(claimToken);
    return (
      <ClaimShell kicker="CLI readiness" title="Claim your readiness report.">
        <ReadinessPreview report={report} summary={summary} />
        <div className={s.actions}>
          <a href={`/sign-up?next=${encodeURIComponent(next)}`}>
            <Button variant="primary">Create account</Button>
          </a>
          <a href={`/sign-in?next=${encodeURIComponent(next)}`}>
            <Button variant="secondary">Sign in</Button>
          </a>
        </div>
      </ClaimShell>
    );
  }

  if (claimed) {
    const dashboard = claimed.orgSlug && buildDashboardPath(claimed.orgSlug);
    return (
      <ClaimShell
        kicker="Report claimed"
        title="Your design system is registered."
      >
        <ReadinessPreview report={report} summary={summary} />
        <p className={s.bodyText}>
          The report is now attached to your registered design system. Connect
          GitHub next to keep the catalog current and enable governance checks.
        </p>
        {dashboard ? (
          <Button
            variant="primary"
            onClick={() =>
              navigate({
                to: dashboard as any,
                search: claimed.projectSlug
                  ? ({ project: claimed.projectSlug } as any)
                  : undefined,
              })
            }
          >
            Open Cloud dashboard
          </Button>
        ) : null}
      </ClaimShell>
    );
  }

  return (
    <ClaimShell kicker="CLI readiness" title="Claim your readiness report.">
      <ReadinessPreview report={report} summary={summary} />

      <section className={s.attachPanel}>
        <div className={s.attachHeader}>
          <h2 className={s.attachTitle}>Choose where to attach it</h2>
          <p className={s.bodyText}>
            This creates the registered design-system record Cloud will use for
            source scans, catalog updates, and governance.
          </p>
        </div>
        {memberships && memberships.length > 0 ? (
          <div className={s.orgList}>
            {memberships.map((item) => (
              <Button
                key={item.org._id}
                variant="secondary"
                className={s.orgButton}
                disabled={claimingOrgId === item.org._id}
                onClick={() => finishClaim(item.org._id)}
              >
                {claimingOrgId === item.org._id
                  ? 'Claiming...'
                  : `Use ${item.org.name}`}
              </Button>
            ))}
          </div>
        ) : null}

        <div className={s.formBlock}>
          <p className={s.bodyText}>
            Or create a new organization for this design system.
          </p>
          <Input
            label="Organization name"
            value={orgForm.name}
            onChange={orgForm.setName}
            placeholder={report.packageName ?? 'Acme Design System'}
          />
          {orgForm.error ? (
            <p className={s.errorText}>{orgForm.error}</p>
          ) : null}
          <Button
            variant="primary"
            disabled={orgForm.isSubmitting || !orgForm.name.trim()}
            onClick={createOrgAndClaim}
          >
            {orgForm.isSubmitting ? 'Creating...' : 'Create org and claim'}
          </Button>
        </div>
        {error ? <p className={s.errorText}>{error}</p> : null}
      </section>
    </ClaimShell>
  );
}

function ReadinessPreview({
  report,
  summary,
}: {
  report: {
    title: string;
    packageName?: string;
    score: { overall: number; label: string };
  };
  summary:
    | {
        inventory?: { components?: number; usageGuidance?: number };
        metrics?: {
          apiCoverage?: number;
          exampleCoverage?: number;
          tokenAdoption?: number | null;
          governanceFindings?: number | null;
        };
        topFindings?: Array<{ ruleId: string; occurrences: number }>;
      }
    | undefined;
}) {
  return (
    <section className={s.preview}>
      <p className={s.privacyLine}>
        <span className={s.privacyDot} aria-hidden="true" />
        Metadata-only CLI report. No source code was uploaded.
      </p>
      <div className={s.scoreBlock}>
        <div className={s.score}>
          <span>{report.score.overall}</span>
          <span className={s.scoreTotal}>/100</span>
        </div>
        <h2 className={s.packageName}>{report.packageName ?? report.title}</h2>
        <p className={s.scoreLabel}>{report.score.label}</p>
      </div>
      <div className={s.metricGrid}>
        <Metric label="Components" value={summary?.inventory?.components} />
        <Metric
          label="API coverage"
          value={formatPercent(summary?.metrics?.apiCoverage)}
        />
        <Metric
          label="Examples"
          value={formatPercent(summary?.metrics?.exampleCoverage)}
        />
        <Metric
          label="Findings"
          value={summary?.metrics?.governanceFindings ?? '—'}
        />
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={s.metric}>
      <span className={s.metricLabel}>{label}</span>
      <strong className={s.metricValue}>{value ?? '—'}</strong>
    </div>
  );
}

function formatPercent(value: number | null | undefined) {
  return typeof value === 'number' ? `${Math.round(value)}%` : '—';
}

function ClaimShell({
  kicker,
  title,
  children,
}: {
  kicker?: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={s.root}>
      <div className={s.page}>
        <aside className={s.editorial} aria-labelledby="claim-ed-heading">
          <div className={s.wordmark}>
            <FragmentsLogo size={26} />
            <span className={s.wordmarkText}>fragments</span>
          </div>
          <div className={s.editorialBody}>
            <span className={s.eyebrow}>Readiness claim</span>
            <h1 id="claim-ed-heading" className={s.editorialHeading}>
              Your first scan is already done.
            </h1>
            <p className={s.lede}>
              Claim the local report you created in the CLI, then connect the
              source repository when you are ready to keep the catalog current.
            </p>
          </div>
        </aside>
        <div className={s.rule} aria-hidden="true" />
        <main className={s.stage}>
          <div className={s.stageInner}>
            <div className={s.stateHeader}>
              {kicker ? <p className={s.kicker}>{kicker}</p> : null}
              <h2 className={s.title}>{title}</h2>
            </div>
            {children}
          </div>
        </main>
        <span className={s.watermark} aria-hidden="true">
          01
        </span>
      </div>
    </div>
  );
}

interface ClaimDraft {
  claimId: Id<'registrationClaims'>;
  expiresAt: number;
  report: {
    _id: Id<'readinessReports'>;
    title: string;
    packageName?: string;
    score: { overall: number; label: string };
    summary: unknown;
    createdAt: number;
  };
}
